import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ClipboardList, Plus, ImageIcon, Send, ShieldCheck, Upload, X, Activity, UploadCloud, Lock, Printer, User, History } from "lucide-react";

function fileUrl(fid) {
  if (!fid) return "";
  if (fid.startsWith("http")) return fid;
  const token = localStorage.getItem("myclinic_token");
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${encodeURIComponent(token)}`;
}

function PhotoUploader({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("myclinic_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload gagal");
      }
      const data = await resp.json();
      onChange(data.id);
      toast.success(`${label} terupload`);
    } catch (err) {
      toast.error(err.message || "Upload gagal");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {value ? (
        <div className="relative rounded-md border border-border overflow-hidden">
          <img src={fileUrl(value)} alt={label} className="h-40 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-remove`}
            className="absolute top-1 right-1 grid h-7 w-7 place-items-center rounded-full bg-rose-600 text-white shadow"
            title="Hapus foto"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          data-testid={`${testId}-btn`}
          className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 hover:bg-secondary text-sm text-muted-foreground transition-colors"
          disabled={uploading}
        >
          <Upload className="h-5 w-5" />
          {uploading ? "Uploading..." : `Upload ${label}`}
          <span className="text-[10px]">JPG/PNG max 8MB</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" data-testid={testId} className="hidden" onChange={upload} />
    </div>
  );
}

function RecoPicker({ label, options, selected, onChange, testId }) {
  const [q, setQ] = useState("");
  const sel = new Set(selected);
  const selectedItems = options.filter((o) => sel.has(o.id));
  const filtered = q
    ? options.filter((o) => !sel.has(o.id) && (o.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 6)
    : [];
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {selectedItems.map((o) => (
          <span key={o.id} data-testid={`${testId}-chip-${o.id}`} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
            {o.name}
            <button type="button" data-testid={`${testId}-remove-${o.id}`} onClick={() => onChange(selected.filter((x) => x !== o.id))} className="text-indigo-500 hover:text-indigo-800">×</button>
          </span>
        ))}
        {selectedItems.length === 0 && <span className="text-xs text-muted-foreground italic">Belum ada rekomendasi</span>}
      </div>
      <div className="relative">
        <Input data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cari ${label.toLowerCase()}...`} className="h-8 text-sm" />
        {filtered.length > 0 && (
          <div className="absolute z-50 mt-1 max-h-44 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
            {filtered.map((o) => (
              <button key={o.id} type="button" data-testid={`${testId}-opt-${o.id}`} onClick={() => { onChange([...selected, o.id]); setQ(""); }} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-secondary">
                {o.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PatientProfileModal({ patient, open, onOpenChange, onAddRecord }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && patient?.id) {
      setLoading(true);
      api.get("/medical-records", { params: { patient_id: patient.id } })
        .then((r) => setRecords([...r.data].sort((a, b) => new Date(a.visit_date) - new Date(b.visit_date))))
        .catch(() => setRecords([]))
        .finally(() => setLoading(false));
    }
  }, [open, patient?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="patient-profile-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <User className="h-5 w-5 text-indigo-600" /> Profil Medis — {patient?.name}
          </DialogTitle>
          <DialogDescription>
            {patient?.phone ? `${patient.phone} · ` : ""}Timeline {records.length} kunjungan rekam medis (urutan kronologis, lama → terbaru).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Memuat riwayat...</div>
        ) : records.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="profile-empty">
            <History className="mx-auto mb-2 h-8 w-8 opacity-40" />
            Belum ada riwayat rekam medis untuk pasien ini.
          </div>
        ) : (
          <ol className="relative ml-3 space-y-6 border-l-2 border-indigo-100 py-2" data-testid="profile-timeline">
            {records.map((r) => (
              <li key={r.id} className="relative ml-6" data-testid={`timeline-entry-${r.id}`}>
                <span className="absolute -left-[31px] top-1 grid h-4 w-4 place-items-center rounded-full bg-indigo-600 ring-4 ring-white" />
                <div className="flex flex-wrap items-center gap-2">
                  <time className="text-sm font-semibold">{fmtDate(r.visit_date)}</time>
                  {r.icd10_code && <Badge variant="outline" className="font-mono text-xs text-indigo-700">{r.icd10_code}</Badge>}
                  {r.treatment_name && <Badge variant="secondary" className="text-xs">{r.treatment_name}</Badge>}
                  {r.consent_signed && <Badge variant="outline" className="text-xs text-emerald-700"><ShieldCheck className="mr-1 h-3 w-3" />Consent</Badge>}
                </div>
                <div className="mt-1 text-sm"><span className="text-muted-foreground">Diagnosa:</span> {r.diagnosis || "—"}</div>
                {r.doctor_name && <div className="text-xs text-muted-foreground">Oleh: {r.doctor_name}</div>}
                {r.notes && <p className="mt-1.5 whitespace-pre-wrap rounded-md bg-secondary/50 p-2 text-sm" data-testid={`timeline-notes-${r.id}`}>{r.notes}</p>}
                {(r.before_photo_url || r.after_photo_url) && (
                  <div className="mt-2 flex gap-3">
                    {r.before_photo_url && (
                      <div className="text-center">
                        <a href={fileUrl(r.before_photo_url)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(r.before_photo_url)} alt="Before" className="h-24 w-24 rounded-md border border-border object-cover" />
                        </a>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">Before</div>
                      </div>
                    )}
                    {r.after_photo_url && (
                      <div className="text-center">
                        <a href={fileUrl(r.after_photo_url)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(r.after_photo_url)} alt="After" className="h-24 w-24 rounded-md border border-border object-cover" />
                        </a>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">After</div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Tutup</Button>
          {onAddRecord && (
            <Button data-testid="profile-add-record-btn" onClick={() => onAddRecord(patient)}>
              <Plus className="mr-1.5 h-4 w-4" /> Tambah Record Baru
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicalRecords() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [form, setForm] = useState({
    notes: "", diagnosis: "", diagnosis_free_text: "", icd10_code: "", before_photo_url: "", after_photo_url: "",
    consent_signed: false, consent_notes: "", consent_patient_name: "", consent_date: "",
    consent_category: "Umum", next_visit_at: "",
  });
  const [icd10Codes, setIcd10Codes] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [recTreat, setRecTreat] = useState([]);
  const [recProd, setRecProd] = useState([]);
  useEffect(() => {
    api.get("/treatments").then((r) => setAllTreatments(r.data)).catch(() => {});
    api.get("/inventory", { params: { category: "retail" } }).then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);
  const loadReco = (pid) => {
    if (!pid) { setRecTreat([]); setRecProd([]); return; }
    api.get(`/patients/${pid}`)
      .then((r) => { setRecTreat(r.data.recommended_treatment_ids || []); setRecProd(r.data.recommended_product_ids || []); })
      .catch(() => { setRecTreat([]); setRecProd([]); });
  };
  const [profilePatient, setProfilePatient] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const openProfile = (p) => { setProfilePatient(p); setProfileOpen(true); setSearchFocused(false); };
  useEffect(() => { api.get("/icd10-codes").then((r) => setIcd10Codes(r.data.codes || [])).catch(() => {}); }, []);
  const [consentMeta, setConsentMeta] = useState({ statement: "", templates: [] });
  useEffect(() => { api.get("/consent-templates").then((r) => setConsentMeta(r.data)).catch(() => {}); }, []);
  const currentTemplate = (consentMeta.templates || []).find((t) => t.category === form.consent_category);

  const refresh = () => api.get("/medical-records").then((r) => setRecords(r.data));
  useEffect(() => { refresh(); }, []);

  const [satusehat, setSatusehat] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const loadSatusehat = () => api.get("/satusehat/status").then((r) => setSatusehat(r.data)).catch(() => setSatusehat(null));
  useEffect(() => { loadSatusehat(); }, []);

  const syncSatusehat = async (rec) => {
    setSyncing(rec.id);
    try {
      const { data } = await api.post(`/satusehat/medical-records/${rec.id}/sync`);
      toast.success(`Tersinkron ke SATUSEHAT (${data.mode === "mock" ? "MOCK" : "LIVE"}) — ${data.resources_sent.join(", ")}`);
      refresh();
      loadSatusehat();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal sync");
    } finally {
      setSyncing(null);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/patients", { params: { search, limit: 30 } }).then((r) => setPatients(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setPatientId("");
    setRecTreat([]); setRecProd([]);
    setForm({ notes: "", diagnosis: "", diagnosis_free_text: "", icd10_code: "", before_photo_url: "", after_photo_url: "", consent_signed: false, consent_notes: "", consent_patient_name: "", consent_date: new Date().toISOString().slice(0, 10), consent_category: "Umum", next_visit_at: "" });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (dialogOpen && !editing && patientId) loadReco(patientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const openCreateForPatient = (p) => {
    setProfileOpen(false);
    setEditing(null);
    setPatientId(p.id);
    setPatients((prev) => (prev.some((x) => x.id === p.id) ? prev : [{ id: p.id, name: p.name, phone: p.phone }, ...prev]));
    setForm({ notes: "", diagnosis: "", diagnosis_free_text: "", icd10_code: "", before_photo_url: "", after_photo_url: "", consent_signed: false, consent_notes: "", consent_patient_name: p.name || "", consent_date: new Date().toISOString().slice(0, 10), consent_category: "Umum", next_visit_at: "" });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setPatientId(rec.patient_id);
    loadReco(rec.patient_id);
    setForm({
      notes: rec.notes || "", diagnosis: rec.diagnosis || "", diagnosis_free_text: rec.diagnosis_free_text || "", icd10_code: rec.icd10_code || "",
      before_photo_url: rec.before_photo_url || "", after_photo_url: rec.after_photo_url || "",
      consent_signed: !!rec.consent_signed, consent_notes: rec.consent_notes || "",
      consent_patient_name: rec.consent_patient_name || rec.patient_name || "",
      consent_date: rec.consent_date || new Date().toISOString().slice(0, 10),
      consent_category: rec.consent_category || rec.treatment_category || "Umum",
      next_visit_at: rec.next_visit_at ? rec.next_visit_at.slice(0, 16) : "",
    });
    setDialogOpen(true);
  };

  const printConsent = async (rec) => {
    try {
      const { data } = await api.get(`/medical-records/${rec.id}/consent-doc`);
      const risks = (data.template?.risks || []).map((r) => `<li>${r}</li>`).join("");
      const dob = data.patient.birthday ? `, ${data.patient.birthday}` : "";
      const w = window.open("", "_blank", "width=800,height=900");
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Informed Consent</title>
      <style>
        body{font-family:Georgia,'Times New Roman',serif;color:#111;max-width:720px;margin:32px auto;padding:0 24px;line-height:1.55}
        h1{font-size:20px;text-align:center;margin:0}
        .clinic{text-align:center;border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:18px}
        .clinic .n{font-size:22px;font-weight:bold}
        .clinic .m{font-size:12px;color:#444}
        h2{font-size:15px;margin:20px 0 6px;text-transform:uppercase;letter-spacing:.5px}
        table{width:100%;font-size:13px;border-collapse:collapse;margin:8px 0}
        td{padding:2px 4px;vertical-align:top}
        td.k{width:150px;color:#444}
        .stmt{background:#f7f7f7;border:1px solid #ddd;padding:12px;font-size:13px;margin:12px 0}
        ul{font-size:13px;margin:4px 0 4px 18px}
        .sign{display:flex;justify-content:space-between;margin-top:48px;font-size:13px}
        .sign div{text-align:center;width:45%}
        .line{margin-top:60px;border-top:1px solid #111;padding-top:4px}
        @media print{button{display:none}}
      </style></head><body>
      <div class="clinic"><div class="n">${data.clinic.name}</div><div class="m">${data.clinic.address || ""} ${data.clinic.phone ? "· " + data.clinic.phone : ""}</div></div>
      <h1>${data.template.title}</h1>
      <h2>Identitas Pasien</h2>
      <table>
        <tr><td class="k">Nama</td><td>: ${data.patient.name}</td></tr>
        <tr><td class="k">NIK (KTP)</td><td>: ${data.patient.nik || "-"}</td></tr>
        <tr><td class="k">Jenis Kelamin / Lahir</td><td>: ${data.patient.gender || "-"}${dob}</td></tr>
        <tr><td class="k">No. Telepon</td><td>: ${data.patient.phone || "-"}</td></tr>
        <tr><td class="k">Alamat</td><td>: ${data.patient.address || "-"}</td></tr>
      </table>
      <h2>Tindakan Medis</h2>
      <table>
        <tr><td class="k">Tindakan</td><td>: ${data.treatment_name || "-"}</td></tr>
        <tr><td class="k">Diagnosa/Indikasi</td><td>: ${data.diagnosis || "-"}</td></tr>
        <tr><td class="k">Dokter</td><td>: ${data.doctor_name || "-"}</td></tr>
      </table>
      <p style="font-size:13px">${data.template.procedure}</p>
      <h2>Risiko & Efek Samping</h2>
      <ul>${risks}</ul>
      <h2>Pernyataan Persetujuan</h2>
      <div class="stmt">${data.statement}</div>
      <div class="sign">
        <div><div>Dokter Penanggung Jawab</div><div class="line">( ${data.doctor_name || "................."} )</div></div>
        <div><div>Pasien / Wali</div><div class="line">( ${data.consent_patient_name || data.patient.name} )</div></div>
      </div>
      <p style="font-size:12px;color:#666;margin-top:24px">Dokumen ini dicetak untuk ditandatangani secara manual oleh pasien/wali dan dokter penanggung jawab sebagai bukti persetujuan tindakan medis.</p>
      <button onclick="window.print()" style="margin-top:16px;padding:8px 16px">Cetak</button>
      </body></html>`);
      w.document.close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat dokumen consent");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!editing && !patientId) { toast.error("Pasien wajib"); return; }
    if (!form.icd10_code) { toast.error("Diagnosa ICD-10 wajib dipilih"); return; }
    const payload = {
      ...form,
      next_visit_at: form.next_visit_at ? new Date(form.next_visit_at).toISOString() : null,
    };
    try {
      if (editing) {
        await api.patch(`/medical-records/${editing.id}`, payload);
        toast.success("Rekam medis diperbarui");
      } else {
        await api.post("/medical-records", { ...payload, patient_id: patientId });
        toast.success("Rekam medis dibuat");
      }
      const pid = editing ? editing.patient_id : patientId;
      if (pid) {
        try {
          await api.patch(`/patients/${pid}/recommendations`, { recommended_treatment_ids: recTreat, recommended_product_ids: recProd });
        } catch { /* rekomendasi opsional */ }
      }
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  const sendReminder = async (rec) => {
    try {
      await api.post("/notifications", {
        patient_id: rec.patient_id,
        channel: rec.patient_email ? "email" : "wa",
        category: "next_visit",
        subject: `Reminder konsultasi lanjutan dari ${user?.name}`,
        body: `Halo ${rec.patient_name}, dr. mengingatkan jadwal kontrol Anda. Mohon konfirmasi jadwal ulang ke klinik kami.`,
      });
      toast.success(`Reminder dikirim ke ${rec.patient_name}`);
    } catch {
      toast.error("Gagal mengirim reminder");
    }
  };

  const filteredRecords = records.filter((r) =>
    !search ? true : (r.patient_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="medical-records-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Rekam Medis</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Riwayat medis, foto before/after, dan informed consent.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Input
              placeholder="Cari pasien..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              className="w-[260px]"
              data-testid="mr-search"
            />
            {searchFocused && search && patients.length > 0 && (
              <div className="absolute z-50 mt-1 max-h-72 w-[260px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="mr-patient-search-results">
                {patients.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`mr-patient-result-${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => openProfile({ id: p.id, name: p.name, phone: p.phone })}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary"
                  >
                    <span className="truncate font-medium">{p.name}</span>
                    <span className="text-xs text-muted-foreground">{p.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {user?.role === "doctor" && (
            <Button data-testid="add-mr-btn" onClick={openCreate}>
              <Plus className="mr-1.5 h-4 w-4" /> Tambah Record
            </Button>
          )}
        </div>
      </div>

      {satusehat && (
        <Card data-testid="satusehat-banner" className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-600 text-white">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  Integrasi SATUSEHAT (Kemenkes)
                  <Badge data-testid="satusehat-mode" className={satusehat.is_live ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                    {satusehat.is_live ? "LIVE" : "MOCK"} · {satusehat.env}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  FHIR R4: {satusehat.resources.join(", ")} · {satusehat.synced_count} record tersinkron
                </div>
              </div>
            </div>
            <Badge variant="outline" className="text-xs text-rose-700 border-rose-200" data-testid="satusehat-financial-block">
              <Lock className="mr-1 h-3 w-3" /> Akses data finansial diblokir
            </Badge>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> {filteredRecords.length} rekam medis
          </CardTitle>
          <CardDescription>Kirim reminder ke pasien atau klik thumbnail foto untuk lihat full.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pasien</TableHead>
                <TableHead>Tgl Kunjungan</TableHead>
                <TableHead>Dokter</TableHead>
                <TableHead>Diagnosa</TableHead>
                <TableHead>Before / After</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Next Visit</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecords.slice(0, 200).map((r) => (
                <TableRow key={r.id} data-testid={`mr-${r.id}`}>
                  <TableCell>
                    <button
                      type="button"
                      data-testid={`mr-open-profile-${r.patient_id}`}
                      onClick={() => openProfile({ id: r.patient_id, name: r.patient_name, phone: r.patient_phone })}
                      className="text-left"
                      title="Lihat profil & timeline medis"
                    >
                      <div className="text-sm font-medium text-indigo-700 hover:underline">{r.patient_name}</div>
                      <div className="text-xs text-muted-foreground">{r.patient_phone}</div>
                    </button>
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(r.visit_date)}</TableCell>
                  <TableCell className="text-xs">{r.doctor_name}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate">
                    {r.icd10_code ? <span className="font-mono text-indigo-700">{r.icd10_code}</span> : null} {r.diagnosis || "—"}
                    {r.diagnosis_free_text ? <span className="block italic text-muted-foreground">{r.diagnosis_free_text}</span> : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      {r.before_photo_url ? (
                        <a href={fileUrl(r.before_photo_url)} target="_blank" rel="noreferrer" title="Before photo">
                          <img src={fileUrl(r.before_photo_url)} alt="Before" className="h-10 w-10 object-cover rounded border border-border" />
                        </a>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                      {r.after_photo_url && (
                        <a href={fileUrl(r.after_photo_url)} target="_blank" rel="noreferrer" title="After photo">
                          <img src={fileUrl(r.after_photo_url)} alt="After" className="h-10 w-10 object-cover rounded border border-border" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.consent_signed ? (
                      <Badge variant="outline" className="text-xs text-emerald-700"><ShieldCheck className="mr-1 h-3 w-3" /> Signed</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-amber-700">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(r.next_visit_at)}</TableCell>
                  <TableCell>
                    {r.satusehat_synced ? (
                      <Badge variant="outline" className="text-xs text-emerald-700" data-testid={`satusehat-synced-${r.id}`} title={r.satusehat_encounter_id}>
                        <ShieldCheck className="mr-1 h-3 w-3" /> Synced
                      </Badge>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`satusehat-sync-${r.id}`} disabled={syncing === r.id} onClick={() => syncSatusehat(r)}>
                        <UploadCloud className="mr-1 h-3.5 w-3.5" /> {syncing === r.id ? "..." : "Kirim"}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" data-testid={`reminder-${r.id}`} onClick={() => sendReminder(r)} title="Kirim reminder">
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`print-consent-${r.id}`} onClick={() => printConsent(r)} title="Cetak informed consent">
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      {(["owner", "super_admin"].includes(user?.role) || (user?.role === "doctor" && r.doctor_id === user?.id)) && (
                        <Button size="sm" variant="outline" data-testid={`edit-mr-${r.id}`} onClick={() => openEdit(r)}>Edit</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRecords.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-10">Belum ada rekam medis.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Edit Rekam Medis" : "Tambah Rekam Medis"}</DialogTitle>
            <DialogDescription>Lengkapi catatan klinis, upload foto before/after, dan informed consent.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <div>
                <Label>Pasien *</Label>
                <Select value={patientId} onValueChange={setPatientId}>
                  <SelectTrigger data-testid="mr-patient-select"><SelectValue placeholder="Pilih pasien" /></SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.phone}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Diagnosa (ICD-10) *</Label>
              <Select value={form.icd10_code} onValueChange={(v) => setForm({ ...form, icd10_code: v, diagnosis: (icd10Codes.find((c) => c.code === v)?.display) || form.diagnosis })}>
                <SelectTrigger data-testid="mr-icd10-select"><SelectValue placeholder="Pilih diagnosa ICD-10" /></SelectTrigger>
                <SelectContent>
                  {icd10Codes.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code} — {c.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">Diagnosa dipilih dari daftar ICD-10 (standar SATUSEHAT Kemenkes).</p>
            </div>
            <div>
              <Label>Detail Keluhan Ringan / Catatan Diagnosa (opsional)</Label>
              <Textarea
                data-testid="mr-diagnosis-freetext"
                value={form.diagnosis_free_text}
                onChange={(e) => setForm({ ...form, diagnosis_free_text: e.target.value })}
                rows={2}
                placeholder="Tulis keluhan ringan pasien secara bebas jika tidak ada di daftar ICD-10 (mis. kulit kusam, dehidrasi ringan, tekstur kasar)"
              />
              <p className="mt-1 text-xs text-muted-foreground">Melengkapi diagnosa terpilih (mis. untuk kategori Z00 - Pemeriksaan Kulit Umum / Healthy Skin).</p>
            </div>
            <div>
              <Label>Catatan Klinis</Label>
              <Textarea data-testid="mr-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <PhotoUploader
                label="Before"
                value={form.before_photo_url}
                onChange={(v) => setForm({ ...form, before_photo_url: v })}
                testId="mr-before-upload"
              />
              <PhotoUploader
                label="After"
                value={form.after_photo_url}
                onChange={(v) => setForm({ ...form, after_photo_url: v })}
                testId="mr-after-upload"
              />
            </div>
            <div>
              <Label>Next Visit</Label>
              <Input type="datetime-local" data-testid="mr-next-input" value={form.next_visit_at} onChange={(e) => setForm({ ...form, next_visit_at: e.target.value })} />
            </div>
            <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-3" data-testid="mr-reco-section">
              <div className="text-sm font-medium flex items-center gap-1.5 text-indigo-800">
                <ClipboardList className="h-4 w-4" /> Rekomendasi untuk Kasir (POS)
              </div>
              <p className="text-xs text-muted-foreground -mt-1.5">
                Treatment/produk yang direkomendasikan akan otomatis masuk ke keranjang POS saat FO memilih pasien ini.
              </p>
              <RecoPicker label="Treatment Direkomendasikan" options={allTreatments} selected={recTreat} onChange={setRecTreat} testId="mr-reco-treatments" />
              <RecoPicker label="Produk Direkomendasikan" options={allProducts} selected={recProd} onChange={setRecProd} testId="mr-reco-products" />
            </div>
            <div className="rounded-md border border-border bg-secondary/40 p-3 space-y-3">
              <div className="text-sm font-medium flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-indigo-600" /> Informed Consent Digital
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kategori Tindakan</Label>
                  <Select value={form.consent_category} onValueChange={(v) => setForm({ ...form, consent_category: v })}>
                    <SelectTrigger data-testid="mr-consent-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(consentMeta.templates || []).map((t) => (
                        <SelectItem key={t.category} value={t.category}>{t.category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tanggal Persetujuan</Label>
                  <Input type="date" data-testid="mr-consent-date" value={form.consent_date} onChange={(e) => setForm({ ...form, consent_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Nama Penandatangan (Pasien/Wali)</Label>
                <Input data-testid="mr-consent-name" value={form.consent_patient_name} onChange={(e) => setForm({ ...form, consent_patient_name: e.target.value })} placeholder="Nama sesuai KTP" />
              </div>
              {currentTemplate && (
                <div className="rounded border border-indigo-100 bg-indigo-50/60 p-2.5 text-xs text-muted-foreground">
                  <div className="font-medium text-indigo-800">{currentTemplate.title}</div>
                  <div className="mt-1">{currentTemplate.procedure}</div>
                  <ul className="mt-1 list-disc pl-4">
                    {(currentTemplate.risks || []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="mr-consent-checkbox"
                  checked={form.consent_signed}
                  onChange={(e) => setForm({ ...form, consent_signed: e.target.checked })}
                />
                <span className="text-sm font-medium">Pasien telah menyetujui informed consent</span>
              </label>
              <Input placeholder="Catatan consent (opsional)..." value={form.consent_notes} onChange={(e) => setForm({ ...form, consent_notes: e.target.value })} />
              {editing && (
                <Button type="button" variant="outline" size="sm" data-testid="mr-print-consent-dialog" onClick={() => printConsent(editing)}>
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Cetak Form Consent
                </Button>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="mr-save-btn">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PatientProfileModal
        patient={profilePatient}
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onAddRecord={user?.role === "doctor" ? openCreateForPatient : null}
      />
    </div>
  );
}
